// src/components/shared/BarraFiltros.jsx
import React, { useEffect, useId, useRef } from "react";

/**
 * Barra de filtros reutilizable (buscador + rangos + personalizado + por página)
 *
 * Props:
 * - microcopy: string
 * - placeholderBusqueda: string
 * - busqueda, setBusqueda
 * - rangoSeleccionado, setRangoSeleccionado: '24h'|'5d'|'7d'|'14d'|'todos'|'personalizado'
 * - fechasPersonalizadas, setFechasPersonalizadas: {inicio, fin}
 * - porPagina, setPorPagina: number
 * - chipsEstado?: [{id,label,icon,count,vista,activo}], onChipClick?: (chip)=>void
 * - stickyTopClass?: string -> clase tailwind para ajustar el offset del sticky (por defecto top-24)
 */
export default function BarraFiltros({
  microcopy,
  placeholderBusqueda = "Buscar…",
  busqueda,
  setBusqueda,
  rangoSeleccionado,
  setRangoSeleccionado,
  fechasPersonalizadas,
  setFechasPersonalizadas,
  porPagina,
  setPorPagina,
  chipsEstado = null,
  onChipClick = () => {},
  stickyTopClass = "top-24",
}) {
  const uid = useId();
  const detailsId = `filtros-personalizados-${uid}`;
  const detailsRef = useRef(null);

  // Cerrar el <details> si cambiamos a un rango que no sea "personalizado"
  useEffect(() => {
    if (rangoSeleccionado !== "personalizado" && detailsRef.current?.open) {
      try { detailsRef.current.open = false; } catch {}
    }
  }, [rangoSeleccionado]);

  // Facilita limpiar con ESC en el input de búsqueda
  const onSearchKeyDown = (e) => {
    if (e.key === "Escape" && busqueda) {
      e.preventDefault();
      setBusqueda("");
    }
  };

  return (
    <div
      className={[
        "sticky z-30",
        stickyTopClass,
        "bg-white/70 backdrop-blur supports-[backdrop-filter]:backdrop-blur",
        "border border-white/50 rounded-2xl shadow-sm",
        "px-3 sm:px-4 pt-2 pb-3",
      ].join(" ")}
    >
      {/* Chips opcionales (p.ej. estados en Solicitudes) */}
      {Array.isArray(chipsEstado) && chipsEstado.length > 0 && (
        <div
          className="flex flex-wrap items-center gap-2 mb-2"
          role="tablist"
          aria-label="Filtros rápidos por estado"
        >
          {chipsEstado.map((c) => (
            <button
              key={c.id}
              type="button"
              role="tab"
              aria-selected={!!c.activo}
              onClick={() => onChipClick(c)}
              className={[
                "min-h-[38px] px-3 py-1.5 rounded-xl text-sm border transition inline-flex items-center gap-2",
                c.activo
                  ? "bg-blue-600 text-white border-blue-600 shadow"
                  : "bg-white/90 text-gray-700 border-gray-200 hover:border-gray-300",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300",
              ].join(" ")}
              title={c.label}
            >
              {c.icon && <span aria-hidden>{c.icon}</span>}
              <span>{c.label}</span>
              {typeof c.count === "number" && (
                <span className="text-xs opacity-80">({c.count})</span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Microcopy */}
      {microcopy && (
        <p
          className="text-xs sm:text-sm text-gray-600 flex items-center gap-2 mb-2"
          aria-live="polite"
        >
          <span aria-hidden>📊</span>
          <span>{microcopy}</span>
        </p>
      )}

      {/* Buscador + rangos + por página */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 sm:gap-4">
        {/* Buscador */}
        <div className="relative w-full lg:flex-1 min-w-[220px]">
          <label htmlFor={`bf-busqueda-${uid}`} className="sr-only">
            {placeholderBusqueda}
          </label>
          <span
            className="absolute left-3 top-1/2 -translate-y-1/2 select-none"
            aria-hidden
          >
            🔍
          </span>
          <input
            id={`bf-busqueda-${uid}`}
            type="text"
            placeholder={placeholderBusqueda}
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            onKeyDown={onSearchKeyDown}
            className="w-full pl-10 pr-3 py-2 rounded-xl border border-gray-300 bg-white/90 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Controles de rango + por página */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          {[
            { id: "24h", label: "24h" },
            { id: "5d", label: "5 días" },
            { id: "7d", label: "7 días" },
            { id: "14d", label: "14 días" },
            { id: "todos", label: "Todo" },
          ].map(({ id, label }) => (
            <button
              key={id}
              type="button"
              onClick={() => setRangoSeleccionado(id)}
              className={[
                "min-h-[38px] px-3 py-1.5 rounded-xl text-sm border transition",
                rangoSeleccionado === id
                  ? "bg-blue-600 text-white border-blue-600 shadow"
                  : "bg-white/90 text-gray-800 border-gray-200 hover:bg-white",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300",
              ].join(" ")}
              aria-pressed={rangoSeleccionado === id}
            >
              {label}
            </button>
          ))}

          {/* Personalizado */}
          <details ref={detailsRef} className="group">
            <summary
              className={[
                "list-none cursor-pointer inline-flex items-center gap-2 min-h-[38px] px-3 py-1.5 rounded-xl text-sm border transition",
                rangoSeleccionado === "personalizado"
                  ? "bg-blue-600 text-white border-blue-600 shadow"
                  : "bg-white/90 text-gray-800 border-gray-200 hover:bg-white",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300",
              ].join(" ")}
              onClick={(e) => {
                const el = e.currentTarget.parentElement;
                if (!el.open) setRangoSeleccionado("personalizado");
              }}
              aria-controls={detailsId}
              aria-expanded={rangoSeleccionado === "personalizado"}
            >
              <span>Personalizado</span>
              <span className="transition group-open:rotate-180" aria-hidden>
                ▾
              </span>
            </summary>

            <div
              id={detailsId}
              className="mt-2 flex items-center gap-2 sm:gap-3 p-2 rounded-xl border border-gray-200 bg-white/95 shadow-sm"
            >
              <label className="text-xs text-gray-600" htmlFor={`bf-inicio-${uid}`}>
                Inicio
              </label>
              <input
                id={`bf-inicio-${uid}`}
                type="date"
                value={fechasPersonalizadas?.inicio || ""}
                onChange={(e) =>
                  setFechasPersonalizadas((p) => ({ ...p, inicio: e.target.value }))
                }
                className="px-2 py-1.5 rounded-lg border border-gray-300 bg-white/90"
                aria-label="Fecha inicio"
              />
              <span className="text-gray-400" aria-hidden>
                →
              </span>
              <label className="text-xs text-gray-600" htmlFor={`bf-fin-${uid}`}>
                Fin
              </label>
              <input
                id={`bf-fin-${uid}`}
                type="date"
                value={fechasPersonalizadas?.fin || ""}
                onChange={(e) =>
                  setFechasPersonalizadas((p) => ({ ...p, fin: e.target.value }))
                }
                className="px-2 py-1.5 rounded-lg border border-gray-300 bg-white/90"
                aria-label="Fecha fin"
              />
            </div>
          </details>

          {/* Por página */}
          <div className="ml-0 sm:ml-2 inline-flex items-center gap-2">
            <label className="text-sm text-gray-600" htmlFor={`bf-por-pagina-${uid}`}>
              Por página
            </label>
            <select
              id={`bf-por-pagina-${uid}`}
              value={porPagina}
              onChange={(e) => setPorPagina(Number(e.target.value))}
              className="px-2 py-1.5 rounded-lg border border-gray-300 bg-white/90 text-sm"
              aria-label="Resultados por página"
            >
              <option value={4}>4</option>
              <option value={8}>8</option>
              <option value={12}>12</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}